<template>
  <!-- Deleted state: only shown once the asset is confirmed missing. -->
  <div
    v-if="isNotFound"
    class="playlist-layer playlist-item deleted"
    :class="{ active: (isCurrentLayout || isCurrentSampleLayout)}"
  >
    <div class="layer-info">
      <div class="deleted-message">This asset has been deleted, or failed to load.</div>

      <button
        class="tb-btn tb-btn-text sm medium-em ml-1"
        title="Remove item from playlist"
        @click.stop.prevent="removeItem"
      >
        <i class="fas fa-minus-circle"></i>
      </button>
    </div>
  </div>

  <div
    v-else-if="asset"
    class="playlist-layer playlist-item numbered-item asset"
    :class="{ editing: isEditing }"
    :data-test="`asset:${asset._id}`"
    @click="onClickTimelineItem"
  >
    <div class="layer-info">

      <button
        class="drag-handle tb-btn tb-btn-link sm low-em"
        title="Rearrange"
      >
        <span class="fas fa-grip-vertical" />
      </button>

      <span class="playlist-item-number" aria-hidden="true" />

      <div class="layer-title flex-grow ml-2">
        <img
          class="layer-preview mr-2 aspect-[16/9] block object-cover bg-[#d9d9d9]"
          :src="thumbnailUrl"
          :alt="item.title"
          @error="assetError = true"
        >

        <div class="flex-grow no-wrap--ellipsis">
          <div class="title-text">{{ item.title }}</div>
          <div class="description-text">{{ asset.asset_type === 'image' ? 'Image only' : 'Video Only'}}</div>
        </div>
      </div>

      <div class="layer-description capitalize ml-1">
        <duration
          v-if="asset.asset_type === 'image'"
          v-model:duration="item.displayTime"
          class="ml-1"
        />

          <template v-else>
            <button
              class="tb-btn tb-btn-contained sm medium-em ml-1 mr-4 play-panel"
              @click.stop="toggleVideo"
            >
              <span
                class="fas"
                :class="videoPlaying ? 'fa-pause' : 'fa-play'"
              />
            </button>
          </template>
      </div>

      <preview-live-switch :item="item"></preview-live-switch>

      <dropdown position="left" class="more-actions ml-1">
        <template #trigger>
          <button class="tb-btn tb-btn-text sm medium-em" title="More actions">
            <i class="fas fa-ellipsis-v"></i>
          </button>
        </template>

        <template #dropdown-body>
          <button
            class="dropdown-list-item"
            @click="duplicateItem"
          >
            <span class="dropdown-list-item-label">
              Duplicate in Playlist
            </span>
          </button>

          <button
            v-if="canDuplicateInGroup"
            class="dropdown-list-item"
            @click="duplicateItemInGroup"
          >
            <span class="dropdown-list-item-label">
              Duplicate in Segment
            </span>
          </button>

          <button
            v-if="canMergeUp"
            class="dropdown-list-item"
            @click="mergeUp"
            title="Merge with above item in the playlist"
          >
            <span class="dropdown-list-item-label">Merge Up</span>
          </button>

          <button
            v-if="canMergeDown"
            class="dropdown-list-item"
            @click="mergeDown"
            title="Merge with below item in the playlist"
          >
            <span class="dropdown-list-item-label">Merge Down</span>
          </button>

          <hr>

          <button
            class="dropdown-list-item"
            @click="removeItem"
          >
            <span class="dropdown-list-item-label warning">
              Remove
            </span>
          </button>
        </template>
      </dropdown>
    </div>
  </div>

  <!-- Loading state: asset metadata hasn't loaded yet, and we
       haven't confirmed the asset is missing either. -->
  <div
    v-else
    class="playlist-layer playlist-item flex items-center"
    :class="{ active: (isCurrentLayout || isCurrentSampleLayout)}"
  >
    <div class="playlist-layer__skeleton" />

    <div class="layer-info">
      <button
        class="tb-btn tb-btn-text sm medium-em ml-1"
        title="Remove item from playlist"
        @click.stop.prevent="removeItem"
      >
        <i class="fas fa-minus-circle"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import TimelineMixins from '@/js/mixins/timeline-mixins';
import getResizedImageUrl from '@/js/utils/imageUrl';
import PreviewLiveSwitch from '../PreviewLiveSwitch.vue';
import duration from './duration-input/DurationInput.vue';

export default {
  name: 'TimelineAssetComponent',
  mixins: [TimelineMixins],
  props: ['item'],

  components: {
    PreviewLiveSwitch,
    duration,
  },

  data() {
    return {
      assetError: false,
    };
  },

  computed: {
    ...mapState('assets', [
      'assetLookupTable',
      'isLoadingTimelineAssets',
      'assetLoadingMap',
      'assetNotFoundMap',
    ]),

    asset() {
      return this.item && this.assetLookupTable[this.item._id];
    },

    thumbnailUrl() {
      return getResizedImageUrl(
        this.asset?.thumbnail_url || this.asset?.media_url,
        'w',
        150,
      );
    },

    /**
     * Whether the asset metadata is still loading.
     */
    isLoading() {
      return !!(this.item && this.assetLoadingMap[this.item._id]);
    },

    /**
     * Whether the asset has been confirmed missing (deleted).
     */
    isNotFound() {
      return !!(this.item && this.assetNotFoundMap[this.item._id]);
    },
  },
};
</script>
